import { Flex, Grid, Stack } from '@chakra-ui/react';
import {
  dailyHospital61To90,
  dailyHospitalLifetimeReserve,
  partADeductible,
  partBDeductible,
  skilledNursingDays21To100,
} from '@components/crm/utils/irmaa';
import AEPDashboardCard from '@components/dashboard/AEPDashboardCard';
import { DashboardHelp } from '@components/dashboard/DashboardHelp';
import HealthcareSummary from '@components/dashboard/HealthcareSummary';
import InfoCard from '@components/dashboard/InfoCard';
import MedigapDashboardCard from '@components/dashboard/MedigapDashboardCard';
import PlanDashboardCard from '@components/dashboard/PlanDashboardCard';
import PreCallSurveyCard from '@components/dashboard/PreCallSurveyCard';
import QuotesCard from '@components/dashboard/QuotesCard';
import SignUpForMedicareCard from '@components/dashboard/SignUpForMedicareCard';
import { SoaCard } from '@components/dashboard/SoaCard';
import BenefitCard from '@components/dashboard/benefits/BenefitCard';
import { DashboardBenefit } from '@components/dashboard/types';
import SpinnerPage from '@components/icons/SpinnerPage';
import Button, { ButtonVariants } from '@components/interactive/Button';
import DefaultLayout from '@components/layout/layout/DefaultLayout';
import Header from '@components/typography/Header';
import { Routes } from '@constants/routes';
import { antlerStyle } from '@constants/styles';
import { useAuthenticatedContextProvider } from '@contexts/AuthenticatedContextProvider';
import { Mode, useMode } from '@contexts/ModeContextProvider';
import { faEnvelope, faRefresh } from '@fortawesome/free-solid-svg-icons';
import useWindowDimensions from '@hooks/useWindowDimensions';
import doctorIcon from '@public/flaticons/doctor.png';
import medicineIcon from '@public/flaticons/medicine.svg';
import isPreAep from '@utils/isPreAep';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Confetti from 'react-confetti';

const FsmDashboard = () => {
  // Resources
  const router = useRouter();
  const { isMobile } = useWindowDimensions();
  const {
    authenticatedUser: user,
    isBedrock,
    loading: loadingUser,
    handleLogout,
    localUser,
  } = useAuthenticatedContextProvider();
  const mode = useMode();

  const [confetti, setConfetti] = useState(false);

  useEffect(() => {
    if (router.query && router.query.confetti) {
      router.replace(Routes.dashboard, undefined, { shallow: true });
      setConfetti(true);
    }
  }, [router]);

  // Loading State
  if (!user || loadingUser) return <SpinnerPage />;

  const {
    agency,
    appointment,
    aep_ignored_at_2027,
    benefits,
    first_name,
    future_mapd,
    future_medigap,
    future_pdp,
    ma,
    future_ma,
    id,
    mapd,
    medigap,
    pdp,
    pharmacies,
    prescriptions,
    providers,
  } = user;

  // Render Medicare Summary (and update survey card)
  const hasPPPdata = !!prescriptions.length || !!providers.length || !!pharmacies.length;

  // Render Plan Card
  const hasPlans = future_medigap || future_mapd || future_pdp || future_ma || medigap || mapd || pdp || ma;

  // Render AEP Card
  const pdpOrMapdUser = future_pdp || pdp || future_mapd || mapd || future_ma || ma;
  const canShowAepCard = mode === Mode.DEMO || (agency?.showAepCardAt && isPreAep() && !aep_ignored_at_2027);
  const mapdUser = future_mapd || mapd || future_ma || ma;
  const showAepCard = (pdpOrMapdUser && canShowAepCard) || (mapdUser && isPreAep());

  const showPreCallSurveyCard = mode !== Mode.DEMO && (isPreAep() ? !hasPlans : true);

  const msPlan = medigap || future_medigap;
  const mapdPlan = mapd || future_mapd;
  const maPlan = ma || future_ma;
  const pdpPlan = pdp || future_pdp;

  return (
    <DefaultLayout showFooter={false}>
      <Flex px={antlerStyle.px} py={[12, 12, 24, '60px']} bg="darkBackground" w="100%">
        <Stack spacing={6} maxW="1000px" w="100%" mx="auto">
          {/* Display Confetti on on user benefit request 🎉 */}
          {confetti && (
            <Confetti
              height={document.body.scrollHeight}
              initialVelocityY={{ min: 8, max: 10 }}
              recycle={false}
              run={confetti}
              width={document.body.scrollWidth}
              onConfettiComplete={() => {
                setConfetti(false);
              }}
            />
          )}

          <Header as="h3">Welcome, {first_name}</Header>

          <Grid gap={[2, 2, 4]} gridTemplateColumns={['1fr 1fr', '1fr 1fr', '1fr 1fr 1fr 1fr']}>
            {!isBedrock && user.lead_owner && (
              <Button
                variant={ButtonVariants.secondary}
                faIcon={faEnvelope}
                href={`${Routes.invite}?userId=${user.id}&source=dashboard`}
                size="sm"
              >
                {isMobile ? 'Refer a Friend' : 'Refer a Friend'}
              </Button>
            )}
            {localUser.multipleUsers?.length > 1 && (
              <Button variant={ButtonVariants.secondary} faIcon={faRefresh} href={Routes.chooseAccount} size="sm">
                Switch User
              </Button>
            )}
          </Grid>

          <Header as="h4" pt={[2, 2, 4]}>
            Things to Do
          </Header>

          {/* AEP Card */}
          {showAepCard && <AEPDashboardCard plan={hasPlans} />}

          <SoaCard />

          <QuotesCard />

          {showPreCallSurveyCard && <PreCallSurveyCard hasPPPdata={hasPPPdata} appointment={appointment} />}

          {/* Benefits */}
          {(mode === Mode.DEMO || agency?.showOtcCardAt) &&
            benefits?.length &&
            benefits.map((benefit: DashboardBenefit) => (
              <BenefitCard
                key={benefit.type}
                userId={id}
                benefit={benefit}
                onCompleted={() => {
                  setConfetti(true);
                }}
              />
            ))}

          {/* Show if has ppp data */}
          {hasPPPdata && <HealthcareSummary user={user} />}

          {/* Return "Things to Do" or Medicare101 */}
          {!hasPlans && (
            <>
              <Header as="h4" pt={[2, 2, 6]}>
                Medicare 101
              </Header>
              <SignUpForMedicareCard
                title="Sign Up For Medicare Parts A & B"
                description="You can sign up for Parts A & B on the social security website. It's easy and takes ten minutes."
                primaryButtonLabel="Sign Up For Parts A & B"
                primaryButtonId="sign_up_a_b"
                primaryHref="https://secure.ssa.gov/iClaim/rib"
                secondaryButtonLabel="Need help? Here's a video of how to sign up."
                secondaryButtonId="sign_up_a_b_need_help"
                secondaryHref="/resources/how-to-apply-a-b"
              />
              <InfoCard
                title="Medicare Parts A & B"
                link={{
                  elementId: 'estimate-a-b-premium-document',
                  text: 'Estimate your A & B Premium',
                  href: '/medicare-at-a-glance/medicare_at_a_glance_2025.pdf',
                }}
                items={{
                  'Hospital Coverage (Part A)': [
                    `$${partADeductible} deductible per 60 day "Benefit Period"`,
                    `Copays of $${dailyHospital61To90} per day for days 61-90, $${dailyHospitalLifetimeReserve} per day for days 91-150`,
                    `Skilled nursing copay $${skilledNursingDays21To100.toFixed(2)} for days 21-100`,
                  ],
                  'Medical Coverage (Parts B)': [
                    `$${partBDeductible} Calendar Year deductible`,
                    '20% patient responsibility with no annual out-of-pocket maximum',
                  ],
                }}
              />
              <InfoCard
                title="Medicare Supplements"
                items={[
                  'Monthly premium (varies based on plan)',
                  'See 90% of doctors in the U.S.',
                  '"Stand Alone" drug plan purchased separately (may choose from 23 plans)',
                  'Minimal or no out-of-pocket costs except monthly premium for many plans',
                  'Benefits do not change annually',
                ]}
              />
              <InfoCard
                title="Medicare Advantage"
                items={[
                  'No monthly premium with some plans',
                  'Doctor and hospital networks',
                  'Drug plan often included',
                  'Out-of-pocket cost for most services up to OOP limit (ranges from $1,000 to $10,000)',
                  'Benefits change every year',
                ]}
              />
            </>
          )}
          {hasPlans && (
            <Header as="h4" pt={[2, 2, 6]}>
              My Plans
            </Header>
          )}
          {/* PDP Card */}
          {pdpPlan && (
            <PlanDashboardCard activePlan={pdp} futurePlan={future_pdp} icon={medicineIcon} title="Prescription Drug" />
          )}
          {/* Medigap Card */}
          {msPlan && <MedigapDashboardCard activePlan={medigap} futurePlan={future_medigap} title="Medical Coverage" />}
          {/* MAPD Card */}
          {mapdPlan && (
            <PlanDashboardCard
              activePlan={mapd}
              futurePlan={future_mapd}
              icon={doctorIcon}
              title="Medical and Prescription Drug"
            />
          )}
          {/* MA Card */}
          {maPlan && <PlanDashboardCard activePlan={ma} futurePlan={future_ma} icon={doctorIcon} title="Medical" />}
          <DashboardHelp handleLogout={handleLogout} />
        </Stack>
      </Flex>
    </DefaultLayout>
  );
};

export default FsmDashboard;
